import * as React from 'react'
import { Link } from 'react-router-dom'
import Header from '../components/layouts/Header'
import Footer from '../components/layouts/Footer'
import TaxonomyScreenshot from '../assets/imgs/eu/taxonomy-screenshot.png'
import { HIDE_CONTACT_HEADER_KEY, Themes, useTheme } from '../hooks/useTheme'
import './WorkforceCapabilityIntelligence.css'

const outcomes = [
  'Future skills and capability analysis',
  'Role-to-skill and proficiency mapping',
  'Skills-gap analysis across teams and functions',
  'A living, evidence-led skills taxonomy',
  'Interactive visual views of capability, gaps and workforce actions',
  'A reusable data structure that can evolve as roles and priorities change',
]

const platformViews = [
  'Capability strengths and gaps',
  'Critical and emerging skills',
  'Current vs required proficiency',
  'Team and organisation-level patterns',
  'Recommended actions: Develop, Recruit, Redeploy, Second, Consult or Technology',
]

const uses = [
  'L&D planning',
  'Strategic Workforce Planning',
  'Internal mobility',
  'Recruitment priorities',
  'Succession planning',
  'Emerging-skills analysis',
]

const WorkforceCapabilityIntelligence = () => {
  const { setTheme } = useTheme()

  React.useEffect(() => {
    setTheme(Themes.DARK)
    localStorage.setItem(HIDE_CONTACT_HEADER_KEY, 'true')
  }, [setTheme])

  return (
    <>
      <div id='smooth-wrapper'>
        <div id='smooth-content'>
          <div className='podio'>
            <main className='workforce-capability'>
              <section className='workforce-capability__hero'>
                <div className='box workforce-capability__shell'>
                  <p className='workforce-capability__eyebrow'>
                    Workforce Capability Intelligence
                  </p>
                  <h1>
                    See the skills you have, the skills you need next, and the
                    gaps that matter.
                  </h1>
                  <p className='workforce-capability__lead'>
                    Pod combines workforce analysis with a living skills
                    taxonomy and interactive capability platform.
                  </p>
                  <p className='workforce-capability__intro'>
                    We map strategy, roles, skills and proficiency into a
                    reusable digital model that helps organisations understand
                    current capability, identify future needs and act on the
                    gaps.
                  </p>
                </div>
              </section>

              <section className='workforce-capability__section workforce-capability__outcomes'>
                <div className='box workforce-capability__shell'>
                  <h2>What you get</h2>
                  <ul className='workforce-capability__checklist'>
                    {outcomes.map((outcome) => (
                      <li key={outcome}>
                        <span className='workforce-capability__checkmark' aria-hidden='true' />
                        <span>{outcome}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>

              <section className='workforce-capability__section workforce-capability__system'>
                <div className='box workforce-capability__shell workforce-capability__split'>
                  <div>
                    <h2>From audit to living capability system</h2>
                    <p>
                      Traditional skills audits often end in a spreadsheet.
                    </p>
                    <p>
                      Pod creates a <strong>maintainable digital capability architecture</strong>.
                    </p>
                  </div>
                  <p
                    className='workforce-capability__flow-items'
                    aria-label='Capability architecture flow'
                  >
                    <span>Strategy</span><i>→</i><span>Roles</span><i>→</i><span>Skills</span><i>→</i><span>Proficiency</span><i>→</i><span>Current Capability</span><i>→</i><span>Future Need</span><i>→</i><span>Gap</span><i>→</i><span>Action</span>
                  </p>
                </div>
              </section>

              <section className='workforce-capability__section workforce-capability__views'>
                <div className='box workforce-capability__shell workforce-capability__split'>
                  <div>
                    <h2>The platform can show</h2>
                    <ul className='workforce-capability__checklist'>
                      {platformViews.map((view) => (
                        <li key={view}>
                          <span className='workforce-capability__checkmark' aria-hidden='true' />
                          <span>{view}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className='workforce-capability__note'>
                    <span>From insight to action</span>
                    <p>
                      Understand where capability is strong, where it is at
                      risk, and which workforce action will make the greatest
                      difference.
                    </p>
                  </div>
                </div>
              </section>

              <section className='workforce-capability__section workforce-capability__evidence'>
                <div className='box workforce-capability__shell'>
                  <div className='workforce-capability__evidence-copy'>
                    <h2>Evidence-led. Human-governed.</h2>
                    <p>
                      Each skill can be linked to source evidence, mapped
                      terms, frameworks and confidence indicators.
                    </p>
                    <p>
                      New data can surface emerging skills and changing
                      terminology, while humans remain in control of
                      definitions, mappings and releases.
                    </p>
                  </div>
                  <figure className='workforce-capability__figure'>
                    <img
                      src={TaxonomyScreenshot}
                      alt='Living skills taxonomy and capability explorer'
                    />
                    <figcaption>
                      <strong>Living skills taxonomy and capability explorer</strong>
                      <span>
                        A reusable digital model connecting skills, evidence,
                        roles, proficiency and workforce actions.
                      </span>
                    </figcaption>
                  </figure>
                </div>
              </section>

              <section className='workforce-capability__section workforce-capability__lasting'>
                <div className='box workforce-capability__shell workforce-capability__lasting-content'>
                  <div>
                    <h2>Built to keep working after the project ends</h2>
                    <p>The output is not just a report.</p>
                    <p>
                      Pod leaves organisations with a technology-enabled
                      workforce capability model that can support:
                    </p>
                  </div>
                  <ul className='workforce-capability__checklist'>
                    {uses.map((use) => (
                      <li key={use}>
                        <span className='workforce-capability__checkmark' aria-hidden='true' />
                        <span>{use}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>

              <section className='workforce-capability__cta'>
                <div className='box workforce-capability__shell'>
                  <h2>Build your workforce capability map</h2>
                  <p>
                    Start with a focused engagement and leave with a reusable
                    model for better workforce decisions.
                  </p>
                  <Link className='book-demo-button' to='/contact'>
                    Talk to Pod
                  </Link>
                </div>
              </section>
            </main>
            <Footer theme={Themes.DARK} isEu />
          </div>
        </div>
      </div>
      <Header isEu />
    </>
  )
}

export default WorkforceCapabilityIntelligence
